import { DOCUMENT } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { clearSessionState, logout } from '../store/actions/auth.actions';
import { UserBlockDialogComponent, UserBlockDialogData } from '../shared/components/user-block-dialog.component';
import { Injectable, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, map, takeUntil, timeout } from 'rxjs';
import { ApiService } from './api.service';
import { selectAuthUser } from '../store/selectors/auth.selectors';
import { userBlockChanged } from '../store/block-content';

@Injectable({ providedIn: 'root' })
export class UserBlockService {
  private readonly api = inject(ApiService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly dialogs = inject(Dialog);
  private readonly document = inject(DOCUMENT);
  private readonly actions = inject(Actions);
  readonly user = this.store.selectSignal(selectAuthUser);
  readonly busy = signal(false);
  readonly blockStateReady = signal(false);
  private blockedIds = new Set<number>();
  private blockedActor?: number;
  private blockLookup?: { actor: number; request: Promise<boolean> };
  async refreshBlockedUsers(): Promise<boolean> {
    const actor = this.user()?.id;
    if (!actor) { this.blockStateReady.set(false); return false; }
    if (this.blockLookup?.actor === actor) return this.blockLookup.request;
    this.blockStateReady.set(false);
    const request = firstValueFrom(this.api.getBlockedUsers().pipe(timeout(15000))).then(rows => {
      if (this.user()?.id !== actor) return false;
      this.blockedActor = actor; this.blockedIds = new Set(rows.map(row => row.userId));
      this.blockStateReady.set(true); return true;
    }).catch(() => {
      if (this.user()?.id === actor) this.toast.error(this.translate.instant('BLOCKED_USERS_LOAD_FAILURE'));
      return false;
    }).finally(() => { if (this.blockLookup?.request === request) this.blockLookup = undefined; });
    this.blockLookup = { actor, request }; return request;
  }
  private alreadyBlocked(id: number): boolean {
    return this.blockedActor === this.user()?.id && this.blockedIds.has(id);
  }
  canBlock(id: number): boolean { return id > 0 && !this.alreadyBlocked(id) && !!this.user() && this.user()!.id !== id && this.user()!.status === 'APPROVED'; }
  canUnblock(): boolean { return this.user()?.status === 'APPROVED'; }
  block(id: number, name = ''): Promise<boolean> { return this.change(id, name, true); }
  unblock(id: number, name = ''): Promise<boolean> { return this.change(id, name, false); }
  private async change(id: number, name: string, blocked: boolean): Promise<boolean> {
    if (id <= 0 || this.busy() || (blocked ? !this.canBlock(id) : !this.canUnblock())) return false;
    const actor = this.user()?.id, startUrl = this.router.url;
    let saved = false;
    this.busy.set(true);
    try {
      const data: UserBlockDialogData = { blocked, name, save: async () => {
        if (this.user()?.id !== actor || (blocked ? !this.canBlock(id) : !this.canUnblock())) return false;
        saved = await firstValueFrom((blocked ? this.api.blockUser(id) : this.api.unblockUser(id)).pipe(
          map(() => true), timeout(15000), takeUntil(this.actions.pipe(ofType(logout, clearSessionState)))
        ), { defaultValue: false });
        return saved && this.user()?.id === actor;
      } };
      const confirmed = await firstValueFrom(this.dialogs.open<boolean>(UserBlockDialogComponent, {
        data, role: 'alertdialog', ariaLabelledBy: 'user-block-title', ariaDescribedBy: 'user-block-message',
        width: '420px', maxWidth: 'calc(100vw - 32px)',
        direction: this.document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr',
        autoFocus: 'first-tabbable', restoreFocus: true,
      }).closed);
      if (!confirmed || !saved || this.user()?.id !== actor) return false;
      this.blockedActor = actor;
      if (blocked) this.blockedIds.add(id); else this.blockedIds.delete(id);
      this.store.dispatch(userBlockChanged({ userId: id, blocked }));
      this.toast.success(this.translate.instant(blocked ? 'USER_BLOCK_SUCCESS' : 'USER_UNBLOCK_SUCCESS'));
      if (blocked && this.router.url === startUrl) await this.router.navigate(['/']);
      return true;
    } catch {
      if (!saved) this.toast.error(this.translate.instant(blocked ? 'USER_BLOCK_FAILURE' : 'USER_UNBLOCK_FAILURE'));
      return saved && this.user()?.id === actor;
    } finally { this.busy.set(false); }
  }
}
